// VAPT API layer — wired to the real Django backend at /api/vapt/
// (django_backend/vapt/). Follows the AssetManagementApis.js pattern:
// apiConfig.get/post/patch, URLSearchParams for query params, try/catch
// re-throw so callers see the real axios error (status/message) and can
// surface it in the UI.
//
// Functions in this module use real backend/API data. Unsupported features
// return empty or unavailable states instead of fabricating VAPT records.

import apiConfig from "../ApiConfig";
import { fetchCustomerRequirementsApi } from "./crmApis";
import AiCRMApis from "./AiCRMApis";
import { VAPT_SCAN_TEMPLATES, VAPT_TOOLS_BY_DOMAIN } from "../../../components/VAPT/utils/vaptConstants";

export { VAPT_SCAN_TEMPLATES, VAPT_TOOLS_BY_DOMAIN };

// Leading slash is required: apiConfig's baseURL is "" whenever
// REACT_APP_BACKEND_URL is unset (same-origin deploys, e.g. Dev). With a
// relative "api/vapt" (no leading slash), axios/the browser resolves the
// URL against the CURRENT PAGE path rather than the origin root, so a
// request made from /ai-vapt/projects resolved to
// /ai-vapt/api/vapt/projects/ instead of /api/vapt/projects/ - a 404 that
// only showed up where REACT_APP_BACKEND_URL was empty. Every other
// module's API file already uses a leading slash; this was the outlier.
const BASE = "/api/vapt";

const asList = (data) => (Array.isArray(data) ? data : data?.results || []);

const buildParams = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") params.append(k, v);
  });
  return params;
};

// ────────────────────────────────────────────────────────────────────────
// Entitlement — reuses CRM's EXISTING customer-requirements API
// (crmApis.fetchCustomerRequirementsApi) read-only. Does NOT modify CRM or
// duplicate its logic anywhere else in this module. Mirrors the exact
// pattern CustomerDetailsModal.js / AiCRM/index.js already use:
// fetchCustomerRequirementsApi(customerId, "AdditionalModule") returns an
// object keyed by module name, where "AI VAPT" is the master boolean and
// "AI VAPT Access" is { asset_vapt, external_vapt }.
// ────────────────────────────────────────────────────────────────────────

export const getVaptCustomerEntitlementApi = async (customerId) => {
  if (!customerId) {
    return { vaptEnabled: false, assetVaptEnabled: false, externalVaptEnabled: false };
  }
  try {
    const addOnModules = await fetchCustomerRequirementsApi(customerId, "AdditionalModule");
    const vaptEnabled = !!addOnModules?.["AI VAPT"];
    const vaptAccess = addOnModules?.["AI VAPT Access"] || {};
    return {
      vaptEnabled,
      assetVaptEnabled: vaptEnabled && !!vaptAccess.asset_vapt,
      externalVaptEnabled: vaptEnabled && !!vaptAccess.external_vapt,
    };
  } catch (error) {
    // Fail closed — no entitlement on error, matching the backend's
    // IsVAPTAuthorized "fails closed" convention.
    return { vaptEnabled: false, assetVaptEnabled: false, externalVaptEnabled: false };
  }
};

// ────────────────────────────────────────────────────────────────────────
// Dashboard — GET /api/vapt/dashboard/?customer_id=
// ────────────────────────────────────────────────────────────────────────

export const getVaptDashboardApi = async (customerId) => {
  try {
    const params = buildParams({ customer_id: customerId });
    const response = await apiConfig.get(`${BASE}/dashboard/?${params}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Risk score — GET /api/vapt/risk-score/?project_id=&customer_id=
// Real, calculated 0-100 score per project from vapt.risk_service
// (Critical=10/High=7/Medium=4/Low=1 weighted, capped at 100).
// ────────────────────────────────────────────────────────────────────────

export const getVaptRiskScoreApi = async (filters = {}) => {
  try {
    const params = buildParams(filters);
    const response = await apiConfig.get(`${BASE}/risk-score/?${params}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Requests — GET/POST /api/vapt/requests/, POST .../approve/, .../reject/
// ────────────────────────────────────────────────────────────────────────

export const listVaptRequestsApi = async (filters = {}) => {
  try {
    const params = buildParams(filters);
    const response = await apiConfig.get(`${BASE}/requests/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

export const getVaptRequestApi = async (requestId) => {
  try {
    const response = await apiConfig.get(`${BASE}/requests/${requestId}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createVaptRequestApi = async (payload) => {
  try {
    const response = await apiConfig.post(`${BASE}/requests/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const approveVaptRequestApi = async (requestId, payload = {}) => {
  try {
    const response = await apiConfig.post(`${BASE}/requests/${requestId}/approve/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const rejectVaptRequestApi = async (requestId, payload = {}) => {
  try {
    const response = await apiConfig.post(`${BASE}/requests/${requestId}/reject/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Projects — GET/POST /api/vapt/projects/, GET/PATCH .../{project_id}/,
// POST .../from-assets/
// ────────────────────────────────────────────────────────────────────────

export const listVaptProjectsApi = async (filters = {}) => {
  try {
    const params = buildParams(filters);
    const response = await apiConfig.get(`${BASE}/projects/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

export const getVaptProjectApi = async (projectId) => {
  try {
    const response = await apiConfig.get(`${BASE}/projects/${projectId}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Only ever creates engagement_type="external" projects — the backend
// hard-rejects engagement_type="asset" here (400: "Asset VAPT projects
// cannot be created manually - they originate from Asset Manager"), and
// rejects "external" with a 400 if the customer's externalVaptEnabled
// entitlement is false. Both messages are real server responses; the
// caller (VaptProjects.js) surfaces error.response.data.error/detail
// directly in its error banner rather than a generic message.
export const createVaptProjectApi = async (payload) => {
  try {
    const response = await apiConfig.post(`${BASE}/projects/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateVaptProjectApi = async (projectId, payload) => {
  try {
    const response = await apiConfig.patch(`${BASE}/projects/${projectId}/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// The ONLY endpoint allowed to create engagement_type="asset" projects.
// Body: {customer_id, created_by, assets: [{asset_ref, name, target_type,
// domain, environment, ...}], name, description}.
export const createVaptProjectFromAssetsApi = async (payload) => {
  try {
    const response = await apiConfig.post(`${BASE}/projects/from-assets/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Real Asset Manager integration path — takes just {customer_id} and the
// backend fetches that customer's actual synced assets from ai_cspm's
// CloudAsset collection itself (populated by Asset Manager's connector
// sync), then creates the Asset VAPT project + targets from them. Returns
// {created: false, message: "..."} (still a 200, not an error) if the
// customer has zero synced assets yet. VaptProjects.js calls this when a
// customer with assetVaptEnabled has no Asset VAPT projects, instead of
// requiring a manual asset picker.
export const syncVaptProjectFromAssetManagerApi = async (customerId) => {
  try {
    const response = await apiConfig.post(`${BASE}/projects/sync-from-asset-manager/`, {
      customer_id: customerId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Automatic engagement/target ensure — alias over the same persisting
// sync-from-asset-manager endpoint (AssetVAPTService.ensure_asset_vapt_engagement
// is idempotent server-side: reuses the existing project/targets if one
// already exists rather than creating a duplicate, and additively
// reconciles new assets). Callers (VaptCreateScan.js) use this name to make
// clear the call is "make sure the engagement exists", not "force a fresh
// sync" — same request either way.
export const ensureVaptAssetEngagementApi = syncVaptProjectFromAssetManagerApi;

// POST /api/vapt/projects/{project_id}/auto-scan/ — the automatic
// "Run Scan Now" action: ensures the engagement/targets exist, reuses any
// already in-flight scan for this project (idempotent — never creates a
// duplicate in-flight scan), or creates + launches a new one against the
// project's real eligible targets with tools derived server-side from the
// seeded ToolRegistry. Returns the scan (new or existing).
export const autoScanVaptProjectApi = async (projectId) => {
  try {
    const response = await apiConfig.post(`${BASE}/projects/${projectId}/auto-scan/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Asset Manager (live, read-only) — GET /api/vapt/asset-manager/assets/
// ?customer_id= — a live, non-persisting view of the customer's real
// ai_cspm.CloudAsset inventory classified for VAPT purposes. Unlike
// syncVaptProjectFromAssetManagerApi above, this creates NO VAPTProject/
// VAPTTarget rows — safe to call on every page load, and returns real data
// even with zero VAPTProject records in the database.
// ────────────────────────────────────────────────────────────────────────

export const getVaptAssetManagerAssetsApi = async (customerId) => {
  try {
    const params = buildParams({ customer_id: customerId });
    const response = await apiConfig.get(`${BASE}/asset-manager/assets/?${params}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Scope — GET/POST /api/vapt/scope/?project_id=
// ────────────────────────────────────────────────────────────────────────

export const listVaptScopeApi = async (projectId) => {
  try {
    const params = buildParams({ project_id: projectId });
    const response = await apiConfig.get(`${BASE}/scope/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// VAPTScope.project is a mongoengine ReferenceField — the backend expects
// the project's Mongo `id` (ObjectId string, present on every project
// object returned by getVaptProjectApi/listVaptProjectsApi as `.id`), not
// the human-readable `project_id` business key. Callers pass the project's
// `id` here.
export const createVaptScopeApi = async (payload) => {
  try {
    const response = await apiConfig.post(`${BASE}/scope/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Targets — GET/POST /api/vapt/targets/?project_id=&domain=
// ────────────────────────────────────────────────────────────────────────

export const listVaptTargetsApi = async (filters = {}) => {
  try {
    const params = buildParams(filters);
    const response = await apiConfig.get(`${BASE}/targets/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// Same ReferenceField note as createVaptScopeApi — payload.project must be
// the project's Mongo `id`.
export const createVaptTargetApi = async (payload) => {
  try {
    const response = await apiConfig.post(`${BASE}/targets/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Authorizations — GET/POST /api/vapt/authorizations/?project_id=
// ────────────────────────────────────────────────────────────────────────

export const listVaptAuthorizationsApi = async (projectId) => {
  try {
    const params = buildParams({ project_id: projectId });
    const response = await apiConfig.get(`${BASE}/authorizations/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// payload.project / payload.target must be Mongo `id`s (see note above).
// NOTE (H3): `status` / `approved_by` / `approved_at` are server-controlled
// and ignored if sent here - a new authorization is always created
// 'Pending'. Approval/rejection is a separate, permission-checked action
// (approveVaptAuthorizationApi / rejectVaptAuthorizationApi below), wired
// into VaptProjectDetail's Scope & Targets panel and the project-create
// flow in VaptProjects.
export const createVaptAuthorizationApi = async (payload) => {
  try {
    const response = await apiConfig.post(`${BASE}/authorizations/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// POST /api/vapt/authorizations/<authorization_id>/approve/ - allowed for
// platform_admin / admin (any tenant) and customer_admin (own tenant only,
// enforced server-side); 403 otherwise. Only a 'Pending' or already
// 'Approved' authorization can be approved (Rejected/Expired/Revoked are
// terminal -> 400). Sets status='Approved' + approved_by/approved_at and
// transitions the linked target(s) to 'Authorized' server-side. This is
// the ONLY way a target authorization becomes Approved.
export const approveVaptAuthorizationApi = async (authorizationId) => {
  try {
    const response = await apiConfig.post(`${BASE}/authorizations/${authorizationId}/approve/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// POST /api/vapt/authorizations/<authorization_id>/reject/ - platform_admin only.
export const rejectVaptAuthorizationApi = async (authorizationId) => {
  try {
    const response = await apiConfig.post(`${BASE}/authorizations/${authorizationId}/reject/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Tool registry — GET /api/vapt/tools/?domain=&enabled=true (read-only,
// 29 seeded rows via manage.py seed_vapt_tools)
// ────────────────────────────────────────────────────────────────────────

export const listVaptToolsApi = async (filters = {}) => {
  try {
    const params = buildParams(filters);
    const response = await apiConfig.get(`${BASE}/tools/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// VaptScans.js "Tools" tab used to read a mock registry with fabricated
// fields (supported_domains, execution_adapter, parser, ...). Now points at
// the same real registry as listVaptToolsApi — VaptScans.js was updated to
// read the real ToolRegistry field names (domain, supported_target_types,
// parser_name) instead.
export const listVaptToolRegistryApi = async (filters = {}) => {
  try {
    const params = buildParams(filters.domain ? { domain: filters.domain } : {});
    const response = await apiConfig.get(`${BASE}/tools/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Scans — GET/POST /api/vapt/scans/?project_id=&status=,
// POST .../{scan_id}/launch/
// ────────────────────────────────────────────────────────────────────────

export const listVaptScansApi = async (filters = {}) => {
  try {
    const params = buildParams(filters);
    const response = await apiConfig.get(`${BASE}/scans/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

export const getVaptScanApi = async (scanId) => {
  try {
    const response = await apiConfig.get(`${BASE}/scans/${scanId}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// payload.project must be the project's Mongo `id` (see ReferenceField note
// above) — VaptCreateScan.js resolves this via getVaptProjectApi before
// calling here.
export const createVaptScanApi = async (payload) => {
  try {
    const response = await apiConfig.post(`${BASE}/scans/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Real launch gate: the backend refuses (400) with
// "Scan cannot be launched: one or more targets are not authorized/approved."
// if any source="external" target on the scan's project lacks an Approved
// Authorization. Callers must surface error.response.data.error verbatim.
export const launchVaptScanApi = async (scanId) => {
  try {
    const response = await apiConfig.post(`${BASE}/scans/${scanId}/launch/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Tool executions — GET /api/vapt/tool-executions/?scan_id= (read-only,
// the ONLY source of truth for whether a tool actually ran)
// ────────────────────────────────────────────────────────────────────────

export const listVaptToolExecutionsApi = async (scanId) => {
  try {
    const params = buildParams({ scan_id: scanId });
    const response = await apiConfig.get(`${BASE}/tool-executions/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// No Worker/ScanWorker model exists anywhere in django_backend/vapt/ —
// confirmed via vapt/models.py. Honest empty list; never fabricated.
export const listVaptWorkersApi = async () => {
  return [];
};

// ────────────────────────────────────────────────────────────────────────
// Real, live tool capability status — GET /api/vapt/tools/status/. Unlike
// listVaptToolsApi/listVaptToolRegistryApi (static registry metadata),
// this re-checks each tool's real PATH availability on every call - never
// cached client-side, so the UI can't show a stale "Ready".
// ────────────────────────────────────────────────────────────────────────

export const getVaptToolsStatusApi = async () => {
  try {
    const response = await apiConfig.get(`${BASE}/tools/status/`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Scan Templates — GET/POST /api/vapt/templates/ (vapt.models.ScanConfiguration)
// ────────────────────────────────────────────────────────────────────────

export const listVaptScanTemplatesApi = async () => {
  try {
    const response = await apiConfig.get(`${BASE}/templates/`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Target authorization/scope validation — POST /api/vapt/targets/<id>/validate/
// ────────────────────────────────────────────────────────────────────────

export const validateVaptTargetApi = async (targetId) => {
  try {
    const response = await apiConfig.post(`${BASE}/targets/${targetId}/validate/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Scan cancel — POST /api/vapt/scans/<scan_id>/cancel/
// ────────────────────────────────────────────────────────────────────────

export const cancelVaptScanApi = async (scanId) => {
  try {
    const response = await apiConfig.post(`${BASE}/scans/${scanId}/cancel/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Findings — GET /api/vapt/findings/?project_id=&severity=&status=&
// validation_status=, POST .../{finding_id}/validate/, .../remediation/,
// .../retest/
// ────────────────────────────────────────────────────────────────────────

export const listVaptFindingsApi = async (filters = {}) => {
  try {
    const params = buildParams(filters);
    const response = await apiConfig.get(`${BASE}/findings/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// Composes the finding with its Evidence and Retest history (both separate
// real endpoints, GET /api/vapt/evidence/?finding_id= and
// GET /api/vapt/retests/?finding_id=) so VaptFindingDetail.js's existing
// `data.evidence` read keeps working.
export const getVaptFindingApi = async (findingId) => {
  try {
    const [findingRes, evidenceRes] = await Promise.all([
      apiConfig.get(`${BASE}/findings/${findingId}/`),
      apiConfig.get(`${BASE}/evidence/?${buildParams({ finding_id: findingId })}`),
    ]);
    return { ...findingRes.data, evidence: asList(evidenceRes.data) };
  } catch (error) {
    throw error;
  }
};

export const updateFindingValidationApi = async (findingId, validationStatus) => {
  try {
    const response = await apiConfig.post(`${BASE}/findings/${findingId}/validate/`, {
      validation_status: validationStatus,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// `payload` may be a bare status string (existing call sites pass a status
// string) or a full remediation fields object — both are forwarded as-is,
// matching VAPTFindingViewSet.remediation's accepted field set
// (assigned_to, owner, priority, sla_days, due_date, recommendation, status).
export const updateFindingRemediationApi = async (findingId, payload) => {
  try {
    const body = typeof payload === "string" ? { status: payload } : payload;
    const response = await apiConfig.post(`${BASE}/findings/${findingId}/remediation/`, body);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addFindingRetestApi = async (findingId, retest) => {
  try {
    const response = await apiConfig.post(`${BASE}/findings/${findingId}/retest/`, retest);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Evidence / Remediation / Retests — standalone list/create endpoints
// ────────────────────────────────────────────────────────────────────────

export const listFindingEvidenceApi = async (findingId) => {
  try {
    const params = buildParams({ finding_id: findingId });
    const response = await apiConfig.get(`${BASE}/evidence/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// Evidence.finding is a ReferenceField — payload.finding must be the
// finding's Mongo `id`, which VaptFindingDetail.js already has on the
// loaded `finding` object (returned by getVaptFindingApi above).
export const addFindingEvidenceApi = async (findingId, evidence) => {
  try {
    const response = await apiConfig.post(`${BASE}/evidence/`, { finding: findingId, ...evidence });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const listVaptRemediationApi = async (filters = {}) => {
  try {
    const params = buildParams(filters);
    const response = await apiConfig.get(`${BASE}/remediation/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

export const listVaptRetestsApi = async (findingId) => {
  try {
    const params = buildParams({ finding_id: findingId });
    const response = await apiConfig.get(`${BASE}/retests/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Risk — no dedicated backend model/endpoint; derived client-side from the
// real findings list (severity + validation_status), same approach
// VaptRisk.js already documented for itself.
// ────────────────────────────────────────────────────────────────────────

export const listVaptRiskApi = async (filters = {}) => {
  try {
    const rows = await listVaptFindingsApi(filters);
    return rows.map((f) => ({
      ...f,
      likelihood: f.severity === "Critical" || f.severity === "High" ? "High" : "Medium",
      impact: f.severity,
      businessRisk: f.severity,
    }));
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Reports — GET/POST /api/vapt/reports/?project_id=
// ────────────────────────────────────────────────────────────────────────

export const listVaptReportRecordsApi = async (projectId) => {
  try {
    const params = buildParams({ project_id: projectId });
    const response = await apiConfig.get(`${BASE}/reports/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// payload.project must be the project's Mongo `id` (ReferenceField).
// payload.file_format ("PDF"|"HTML"|"CSV", defaults server-side to "HTML")
// - the backend now actually renders and stores this file (BUG VAPT-003
// fix); the returned record's `status` is only "Generated" once that real
// file exists, "Pending"/"Failed" otherwise - never fake a download.
export const generateVaptReportApi = async (payload) => {
  try {
    const response = await apiConfig.post(`${BASE}/reports/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Real generated file download - GET /api/vapt/reports/<report_id>/download/.
// Only ever call this when the report record's status === "Generated";
// the backend 404s otherwise rather than returning an empty/fake file.
// Returns a Blob the caller can trigger a browser save for.
export const downloadVaptReportApi = async (reportId) => {
  try {
    const response = await apiConfig.get(`${BASE}/reports/${reportId}/download/`, { responseType: "blob" });
    return response.data;
  } catch (error) {
    // With responseType "blob" an error body arrives as a Blob too; decode
    // it so callers can show the backend's real message.
    if (error?.response?.data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await error.response.data.text());
      } catch {
        /* leave the blob as-is */
      }
    }
    throw error;
  }
};

// POST /api/vapt/reports/<report_id>/regenerate/ — re-runs real generation
// for an existing record (e.g. a Failed/legacy report). Status only becomes
// "Generated" once a real file exists.
export const regenerateVaptReportApi = async (reportId, fileFormat) => {
  try {
    const response = await apiConfig.post(`${BASE}/reports/${reportId}/regenerate/`, fileFormat ? { file_format: fileFormat } : {});
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Composite "Reports" page view built from real endpoints: dashboard
// aggregates + findings + scans, scoped to a single customer/project when
// given. No PDF/export or narrative-generation exists on the backend, so
// executive.summary is intentionally left empty (VaptReports.js already
// renders an explicit "not currently available" note for that gap).
export const getVaptReportsApi = async (filters = {}) => {
  try {
    const [dashboard, findings, scans] = await Promise.all([
      getVaptDashboardApi(filters.customer_id),
      listVaptFindingsApi(filters.project_id ? { project_id: filters.project_id } : {}),
      listVaptScansApi(filters.project_id ? { project_id: filters.project_id } : {}),
    ]);
    const bySeverity = dashboard.findings_by_severity || {};
    const topFindings = findings
      .slice()
      .sort((a, b) => Number(b.cvss_score || 0) - Number(a.cvss_score || 0))
      .slice(0, 10);
    // One row per finding that has retests, carrying its attempts — the
    // shape the Retest report table renders (title + retests[]). Flattening
    // to one row per attempt produced duplicate row keys and blank cells.
    const retest = await Promise.all(
      findings.map((f) => listVaptRetestsApi(f.finding_id).then((rows) => ({ ...f, retests: rows })))
    ).then((groups) => groups.filter((f) => f.retests.length > 0));

    return {
      executive: {
        summary: "",
        keyMetrics: {
          totalProjects: dashboard.total_projects,
          activeScans: dashboard.active_scans,
          completedScans: dashboard.completed_scans,
          openFindings: dashboard.open_findings,
          resolvedFindings: dashboard.resolved_findings,
          pendingRetests: dashboard.pending_retests,
          criticalFindings: bySeverity.Critical || 0,
          highFindings: bySeverity.High || 0,
        },
      },
      technical: { totalFindings: findings.length, bySeverity, topFindings },
      findings,
      risk: findings.map((f) => ({ ...f, status: f.validation_status === "Accepted Risk" ? "Accepted" : "Open" })),
      scanExecution: scans,
      retest,
      records: await listVaptReportRecordsApi(filters.project_id),
    };
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Customers — for the Platform Admin customer switcher. Reuses CRM's
// EXISTING lead/customer listing API (AiCRMApis.getCustomers ->
// GET /api/ai-crm/api/v1/leads/), read-only, same reuse rule as
// getVaptCustomerEntitlementApi above. Does not modify CRM.
// ────────────────────────────────────────────────────────────────────────

export const listVaptCustomersApi = async () => {
  try {
    const res = await AiCRMApis.getCustomers({ page: 1, page_size: 200 });
    return asList(res?.data ?? res);
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// VAPT-enabled customers — GET /api/vapt/customers/ (platform_admin only).
// Unlike listVaptCustomersApi above (ALL customers via CRM's lead list),
// this returns only customers with the "AI VAPT" module actually enabled
// (real entitlement dict-walk on the backend, vapt/permissions.py's
// get_customer_vapt_entitlement) — used by VaptDashboard.js's customer
// switcher so Platform Admin never sees non-VAPT customers in that list.
// ────────────────────────────────────────────────────────────────────────

export const listVaptEnabledCustomersApi = async () => {
  try {
    const response = await apiConfig.get(`${BASE}/customers/`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// The sections below have NO backing model/endpoint anywhere in
// django_backend/vapt/ (confirmed against vapt/models.py). They remain
// mock/reference data, each labeled in its own page/component as demo-only
// — never presented as live data. Kept here unchanged from the prior mock
// implementation so the pages that reference them keep working.
// ────────────────────────────────────────────────────────────────────────

// Real, live-computed coverage — GET /api/vapt/coverage/?project_id=
// (BUG VAPT-005 fix, replaces the frontend mock). Requires a single real
// project_id; see vapt/views.py::VAPTCoverageView for exactly how each
// target's Applicable/Implemented/Not Implemented/Not Applicable/
// Unsupported status is derived from real VAPTTarget/ToolExecution rows.
export const getVaptExecutionCoverageApi = async (projectId) => {
  if (!projectId) return null;
  try {
    const response = await apiConfig.get(`${BASE}/coverage/?${buildParams({ project_id: projectId })}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Real multi-project execution coverage (BUG-006 fix — no longer mock).
// Composed from the real single-project GET /api/vapt/coverage/?project_id=
// endpoint for every project the caller can see, then summed. Returns
// { summary: {...totals}, projects: [{ project_id, project_name, summary }] }.
export const listVaptExecutionCoverageApi = async (filters = {}) => {
  try {
    const projects = await listVaptProjectsApi(filters);
    const keys = ["applicable", "implemented", "not_implemented", "not_applicable", "unsupported", "out_of_scope", "blocked", "total"];
    const perProject = await Promise.all(
      projects.map(async (p) => {
        const cov = await getVaptExecutionCoverageApi(p.project_id).catch(() => null);
        const s = cov?.detail?.summary || cov?.summary || {};
        const rows = cov?.detail?.rows || cov?.rows || [];
        return { project_id: p.project_id, project_name: p.name, summary: s, rows };
      })
    );
    const summary = keys.reduce((acc, k) => {
      acc[k] = perProject.reduce((n, pp) => n + (Number(pp.summary[k]) || 0), 0);
      return acc;
    }, {});
    return { summary, projects: perProject };
  } catch (error) {
    throw error;
  }
};

// Real Risk Acceptance list — GET /api/vapt/risk-acceptance/ (BUG VAPT-005
// fix, replaces the frontend mock). Customer-isolated the same way every
// other VAPT list endpoint is (vapt/permissions.py).
export const listVaptRiskAcceptanceApi = async (filters = {}) => {
  try {
    const params = buildParams(filters);
    const response = await apiConfig.get(`${BASE}/risk-acceptance/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

export const createVaptRiskAcceptanceApi = async (payload) => {
  try {
    const response = await apiConfig.post(`${BASE}/risk-acceptance/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const decideVaptRiskAcceptanceApi = async (acceptanceId, decision, notes) => {
  try {
    const response = await apiConfig.post(`${BASE}/risk-acceptance/${acceptanceId}/decide/`, { decision, notes });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Scope Exceptions — real backend (GET/POST /api/vapt/exceptions/, POST
// .../decide/). A deliberate, reviewed, time-bounded carve-out for a
// target - distinct from a plain unauthorized/out-of-scope target.
// ────────────────────────────────────────────────────────────────────────

export const listVaptExceptionsApi = async (filters = {}) => {
  try {
    const params = buildParams(filters);
    const response = await apiConfig.get(`${BASE}/exceptions/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

export const createVaptExceptionApi = async (payload) => {
  try {
    const response = await apiConfig.post(`${BASE}/exceptions/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const decideVaptExceptionApi = async (exceptionId, decision) => {
  try {
    const response = await apiConfig.post(`${BASE}/exceptions/${exceptionId}/decide/`, { decision });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Scheduled Scans — real backend (GET/POST /api/vapt/schedules/, POST
// .../enable/, .../disable/). Backed by Celery Beat's per-minute
// vapt-schedule-tick (vapt/scheduler_service.py) - the SAME real
// create_scan_task/_launch_scan path "Start Scan" uses, on a recurring
// cadence, never a second/fake scan-creation path.
// ────────────────────────────────────────────────────────────────────────

export const listVaptSchedulesApi = async (projectId) => {
  try {
    const params = buildParams({ project_id: projectId });
    const response = await apiConfig.get(`${BASE}/schedules/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// payload.project must be the project's Mongo `id` (ReferenceField).
export const createVaptScheduleApi = async (payload) => {
  try {
    const response = await apiConfig.post(`${BASE}/schedules/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const setVaptScheduleEnabledApi = async (scheduleId, enabled) => {
  try {
    const response = await apiConfig.post(`${BASE}/schedules/${scheduleId}/${enabled ? "enable" : "disable"}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateVaptScheduleApi = async (scheduleId, payload) => {
  try {
    const response = await apiConfig.patch(`${BASE}/schedules/${scheduleId}/`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteVaptScheduleApi = async (scheduleId) => {
  try {
    const response = await apiConfig.delete(`${BASE}/schedules/${scheduleId}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ────────────────────────────────────────────────────────────────────────
// Notifications — real, EXISTING platform-wide system
// (notifications/models.py, GET/PUT /api/notifications/), never a VAPT-
// specific mock/duplicate. VAPT's own events (module='vapt') are read the
// same way every other module's notifications are - filtered client-side
// since the shared endpoint doesn't take a `module` query param (kept
// that way deliberately - not modifying the shared notifications app).
// ────────────────────────────────────────────────────────────────────────

export const listVaptNotificationsApi = async (userId) => {
  if (!userId) return [];
  // module=vapt is now filtered server-side (notifications/views.py) so
  // page_size can't silently drop VAPT rows behind a wall of other
  // modules' notifications. The client-side filter is kept as a
  // belt-and-suspenders guard for older backends.
  // Leading slash required - see the BASE constant's comment above: a
  // relative path with no leading slash resolves against the current page
  // path (e.g. /ai-vapt/notifications), not the origin root, producing a
  // 404 at /ai-vapt/api/notifications/....
  const response = await apiConfig.get(
    `/api/notifications/?${buildParams({ to_user_id: userId, module: "vapt", page_size: 200 })}`
  );
  const rows = response.data?.results || [];
  return rows.filter((n) => n.module === "vapt");
};

export const markVaptNotificationReadApi = async (notificationId) => {
  try {
    // Leading slash required - see the BASE constant's comment above.
    const response = await apiConfig.put(`/api/notifications/${notificationId}/read/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// module=vapt scopes "mark all read" to VAPT notifications only - a user's
// CSPM / incident / other-module notifications are never touched.
export const markAllVaptNotificationsReadApi = async (userId) => {
  const response = await apiConfig.put(
    `/api/notifications/read-all/?${buildParams({ to_user_id: userId, module: "vapt" })}`
  );
  return response.data;
};

export const listVaptApplicabilityApi = async (projectId) => {
  return [];
};

export const getVaptCustomerNotificationPreviewApi = async (projectId) => {
  return null;
};

export const getVaptNotificationPreviewApi = async (projectId) => {
  return null;
};

// Real VAPT customer-notification history - GET /api/vapt/customer-notification/
// (module='vapt', event_type='customer_notification' rows in the shared
// app_notifications collection, customer-isolated server-side).
export const listVaptNotificationHistoryApi = async (projectId) => {
  try {
    const params = buildParams({ project_id: projectId });
    const response = await apiConfig.get(`${BASE}/customer-notification/?${params}`);
    return asList(response.data);
  } catch (error) {
    throw error;
  }
};

// Real customer notification - POST /api/vapt/customer-notification/.
// Recipient is resolved server-side from the project's registered customer
// contact (never hardcoded). An in-app notification is always recorded;
// the returned `email_status` is one of "Sent" | "Test Mode (not actually
// delivered)" | "Email provider not configured" | "Failed" - the UI must
// surface it verbatim and never claim "sent" on its own.
export const sendVaptCoverageNotificationApi = async ({ projectId, reportId } = {}) => {
  try {
    const response = await apiConfig.post(`${BASE}/customer-notification/`, {
      project_id: projectId,
      report_id: reportId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAutomaticNotificationConfigApi = async () => {
  return null;
};

export const updateAutomaticNotificationConfigApi = async (payload = {}) => {
  return null;
};
